/**
 * HelpDrawer: the help panel for the page on screen (opened from "?" or a tab's "More help").
 *
 * Page title, menu path, who can open it and the summary; the page's tabs (the open tab expanded);
 * its tours with Start; and the Ask AI box. It does not block the page, so people can read and
 * click at the same time, and it follows the person after "Go to".
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconBook2, IconExternalLink, IconHelp, IconLock, IconPencil, IconPlayerPlay, IconRoute, IconSparkles } from '@tabler/icons-react';
import { Accordion, Alert, Badge, Button, Divider, Drawer, Group, Skeleton, Stack, Text, Title } from '@/components/ui';
import { useHelpContext } from './HelpContext';
import { HelpHtml } from './HelpHtml';
import { AskAiPanel } from './AskAiPanel';
import { findTabHelp, usePageHelp } from './usePageHelp';
import { activeTours } from './helpUtils';
import { HELP_ADMIN_PATH, AI_FEATURE_FLAGS_PATH } from './helpPaths';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  new: { label: 'New', color: 'blue' },
  updated: { label: 'Updated', color: 'violet' },
  completed: { label: 'Done', color: 'teal' },
  dismissed: { label: 'Skipped', color: 'gray' },
};

export function HelpDrawer() {
  const ctx = useHelpContext();
  const pageKey = ctx?.drawer.pageKey ?? null;
  const opened = !!ctx?.drawer.opened && !!pageKey;
  const { page, def, isLoading, error, notWritten, title } = usePageHelp(opened ? pageKey : null);
  const tabKey = ctx?.drawer.tabKey ?? null;
  const [openTab, setOpenTab] = useState<string | null>(tabKey);

  useEffect(() => {
    setOpenTab(tabKey);
  }, [tabKey, pageKey]);

  if (!ctx) return null;

  const storedTabs = [...(page?.tabs ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const tabRows =
    storedTabs.length > 0
      ? storedTabs.map((t) => ({ key: t.tabKey, title: t.title, html: t.bodyHtml ?? null, purpose: def?.tabs.find((d) => d.key === t.tabKey)?.purpose ?? null }))
      : (def?.tabs ?? []).map((t) => ({ key: t.key, title: t.title, html: null as string | null, purpose: t.purpose }));
  const tours = activeTours(page);
  const { tab } = findTabHelp(page, def, ctx.activeTabFor(pageKey) ?? tabKey);
  const menuPath = page?.menuPath || def?.menuPath;
  const accessNote = page?.accessNote || def?.accessNote;

  return (
    <Drawer
      opened={opened}
      onClose={ctx.closeHelp}
      position="right"
      size="md"
      withOverlay={false}
      lockScroll={false}
      trapFocus={false}
      closeOnClickOutside={false}
      closeOnEscape
      shadow="xl"
      title={
        <Group gap="xs" wrap="nowrap">
          <IconHelp size={18} />
          <span>Help</span>
        </Group>
      }
    >
      {pageKey && (
        <Stack gap="lg" pb="xl">
          <Stack gap={4}>
            <Title order={3}>{title}</Title>
            {menuPath && (
              <Text size="xs" c="dimmed">
                {menuPath}
              </Text>
            )}
            {accessNote && (
              <Group gap={4} wrap="nowrap">
                <IconLock size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {accessNote}
                </Text>
              </Group>
            )}
          </Stack>

          {isLoading ? (
            <Stack gap="xs">
              <Skeleton height={14} />
              <Skeleton height={14} />
              <Skeleton height={14} width="70%" />
            </Stack>
          ) : error ? (
            <Alert color="orange" alertVariant="light">
              Help could not be loaded right now. Try again in a moment.
            </Alert>
          ) : notWritten || !page?.summaryHtml ? (
            <Stack gap="xs">
              {def?.purpose && <Text size="sm">{def.purpose}</Text>}
              <Text size="sm" c="dimmed">
                Help for this page is not written yet.
              </Text>
            </Stack>
          ) : (
            <HelpHtml html={page.summaryHtml} />
          )}

          {tabRows.length > 0 && (
            <Stack gap="xs">
              <Group gap={6}>
                <IconBook2 size={16} />
                <Text fw={600} size="sm">
                  Tabs on this page
                </Text>
              </Group>
              <Accordion value={openTab} onChange={setOpenTab} accordionVariant="separated" radius="md">
                {tabRows.map((t) => (
                  <Accordion.Item key={t.key} value={t.key}>
                    <Accordion.Control>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={600}>
                          {t.title}
                        </Text>
                        {ctx.activeTabFor(pageKey) === t.key && (
                          <Badge size="xs" variant="light" color="teal" styles={{ root: { textTransform: 'none' } }}>
                            Open now
                          </Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {t.html ? (
                        <HelpHtml html={t.html} />
                      ) : (
                        <Stack gap={4}>
                          {t.purpose && <Text size="sm">{t.purpose}</Text>}
                          <Text size="xs" c="dimmed">
                            Help for this tab is not written yet.
                          </Text>
                        </Stack>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Stack>
          )}

          <Stack gap="xs">
            <Group gap={6}>
              <IconRoute size={16} />
              <Text fw={600} size="sm">
                Tours
              </Text>
            </Group>
            {tours.length === 0 ? (
              <Text size="sm" c="dimmed">
                No tour for this page yet.
              </Text>
            ) : (
              tours.map((t) => {
                const status = STATUS_LABEL[ctx.tourStatus(t)];
                return (
                  <Group key={t.tourKey} justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="wrap">
                        <Text size="sm" fw={600}>
                          {t.title}
                        </Text>
                        <Badge size="xs" variant="light" color={status.color} styles={{ root: { textTransform: 'none' } }}>
                          {status.label}
                        </Badge>
                      </Group>
                      {t.description && (
                        <Text size="xs" c="dimmed">
                          {t.description}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed">
                        {t.steps.length} {t.steps.length === 1 ? 'step' : 'steps'}
                      </Text>
                    </Stack>
                    <Button size="compact-sm" variant="light" leftSection={<IconPlayerPlay size={14} />} onClick={() => void ctx.startTour(t)}>
                      Start
                    </Button>
                  </Group>
                );
              })
            )}
          </Stack>

          {/* Ask AI: hidden while it is switched off; admins and help editors see how to switch it on. */}
          {(ctx.ask.enabled || (ctx.ask.loaded && ctx.canManageHelp)) && (
            <>
              <Divider />
              <Stack gap="xs">
                <Group gap={6}>
                  <IconSparkles size={16} />
                  <Text fw={600} size="sm">
                    Ask about this page
                  </Text>
                </Group>
                {ctx.ask.enabled ? (
                  <AskAiPanel pageKey={pageKey} page={page} tab={tab} tabKey={ctx.activeTabFor(pageKey) ?? tabKey} focusNonce={ctx.drawer.focusAskNonce} />
                ) : (
                  <AiOffHint isAdmin={ctx.isAdmin} message={ctx.ask.message} />
                )}
              </Stack>
            </>
          )}

          {ctx.canManageHelp && (
            <>
              <Divider />
              <Group>
                <Button component={Link} to={`${HELP_ADMIN_PATH}?page=${encodeURIComponent(pageKey)}`} variant="subtle" size="compact-sm" leftSection={<IconPencil size={14} />}>
                  Edit help for this page
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

/** For admins and help editors when Ask AI is off: what to do about it. */
export function AiOffHint({ isAdmin, message }: { isAdmin: boolean; message?: string | null }) {
  const lead = (message?.trim() || 'AI help is switched off').replace(/([^.!?])$/, '$1.');
  return (
    <Alert color="gray" alertVariant="light" p="sm">
      <Stack gap={6}>
        <Text size="sm">{lead} People do not see Ask AI until the HelpAssistant flag is on.</Text>
        {isAdmin ? (
          <Group>
            <Button component={Link} to={AI_FEATURE_FLAGS_PATH} size="compact-sm" variant="light" rightSection={<IconExternalLink size={14} />}>
              Switch it on in Feature Flags
            </Button>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            Ask an admin to switch on HelpAssistant in AI Management › Administration › Feature Flags.
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
